import { useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { ReferralEvents, trackReferralEvent } from "@/utils/analytics";

export function useReferralValidation() {
  const { user } = useAuth();
  const { toast } = useToast();

  useEffect(() => {
    if (!user) return;

    // Check for pending referral validation after user signs up and confirms email
    const validatePendingReferral = async () => {
      const pendingValidation = localStorage.getItem(
        "pendingReferralValidation"
      );

      if (!pendingValidation) return;

      try {
        const { referralCode, userEmail, timestamp } =
          JSON.parse(pendingValidation);

        // Check if the validation is not too old (24 hours max)
        const validationTime = new Date(timestamp);
        const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);

        if (validationTime < twentyFourHoursAgo) {
          localStorage.removeItem("pendingReferralValidation");
          return;
        }

        // Check if user email matches
        if (userEmail !== user.email) {
          localStorage.removeItem("pendingReferralValidation");
          return;
        }

        // Validate the referral code
        const { data, error } = await supabase.functions.invoke(
          "api/validate-referral",
          {
            body: {
              referralCode: referralCode,
              userEmail: user.email,
            },
          }
        );

        if (error) throw error;

        if (data.valid) {
          trackReferralEvent({
            event: ReferralEvents.CODE_VALIDATED,
            userId: user.id,
            referralCode: referralCode,
          });

          toast({
            title: "Cod de recomandare aplicat",
            description:
              "Felicitări! Ai primit credite bonus pentru că ai fost recomandat.",
          });
        } else {
          trackReferralEvent({
            event: ReferralEvents.CODE_REJECTED,
            userId: user.id,
            referralCode: referralCode,
            metadata: { reason: data.reason },
          });
        }

        // Clean up
        localStorage.removeItem("pendingReferralValidation");
        localStorage.removeItem("pendingReferralCode");
      } catch (error) {
        console.error("Error validating pending referral:", error);
        localStorage.removeItem("pendingReferralValidation");
        localStorage.removeItem("pendingReferralCode");
      }
    };

    // Run validation after a short delay to ensure user is fully authenticated
    const timeoutId = setTimeout(validatePendingReferral, 1000);

    return () => clearTimeout(timeoutId);
  }, [user, toast]);
}
